/**
 * Identity key-chain fold (F4, ADR-12) — resolution IS the fold.
 *
 * Input: identityId + envelopes in log order (ordering is the caller's job —
 * A1). The first envelope is genesis and must carry the identityId,
 * self-certify (deriveDidBeacon(genesis.signingKey) == identityId) and bear a
 * valid strict signature, else the whole fold errors ("not-self-certifying" /
 * "genesis-bad-sig"). Every envelope must carry the identityId, be signed by
 * the current chain head, and verify strictly; failures are rejected
 * (wrong-identity / stale-key / bad-sig / prev-mismatch / malformed-payload)
 * and the fold continues — the fold is total over adversarial logs; only
 * genesis failures abort. rotateKey moves the head (and optionally the
 * encryption key); declareKeys sets the encryption key.
 */
import type { CborValue } from "./cbor.js";
import { deriveDidBeacon } from "./didkey.js";
import { verifyEnvelope } from "./sign.js";

export type RejectReason =
  | "wrong-identity"
  | "stale-key"
  | "bad-sig"
  | "prev-mismatch"
  | "malformed-payload";

export interface FoldResult {
  signingKey: string;
  encryptionKey: string | null;
  accepted: string[];
  rejected: { tid: string; reason: RejectReason }[];
}

const ROTATE_KEY = "app.aid.identity.rotateKey";
const DECLARE_KEYS = "app.aid.identity.declareKeys";

export async function foldIdentity(
  identityId: string,
  envelopes: Record<string, CborValue>[],
): Promise<FoldResult> {
  if (envelopes.length === 0) throw new Error("identity fold: empty chain");

  const genesis = envelopes[0];
  const genesisKey = genesis.signingKey;
  if (
    typeof genesisKey !== "string" ||
    genesis.identityId !== identityId ||
    (await deriveDidBeacon(genesisKey)) !== identityId
  ) {
    throw new Error("identity fold: not-self-certifying genesis");
  }
  if (!(await verifyEnvelope(genesis, genesisKey))) {
    throw new Error("identity fold: genesis-bad-sig");
  }

  let signingKey = genesisKey; // head established by self-certification
  let encryptionKey: string | null = null;
  let lastRotationTid: string | null = null;
  const accepted: string[] = [];
  const rejected: { tid: string; reason: RejectReason }[] = [];

  for (const env of envelopes) {
    const tid = typeof env.id === "string" ? env.id : "";
    if (env.identityId !== identityId) {
      rejected.push({ tid, reason: "wrong-identity" });
      continue;
    }
    if (env.signingKey !== signingKey) {
      rejected.push({ tid, reason: "stale-key" });
      continue;
    }
    if (!(await verifyEnvelope(env, signingKey))) {
      rejected.push({ tid, reason: "bad-sig" });
      continue;
    }

    const payload =
      typeof env.payload === "object" && env.payload !== null && !Array.isArray(env.payload) &&
      !(env.payload instanceof Uint8Array)
        ? (env.payload as Record<string, CborValue>)
        : {};

    if (env.$type === ROTATE_KEY) {
      // prev, when the KEY is present (any value, including null), must be a
      // string naming the last ACCEPTED rotation's TID; a first rotation must
      // omit the key entirely. Present-key semantics is the one check Go
      // (`_, ok := payload["prev"]`), Rust (map key presence) and TS express
      // identically.
      if ("prev" in payload && (typeof payload.prev !== "string" || payload.prev !== lastRotationTid)) {
        rejected.push({ tid, reason: "prev-mismatch" });
        continue;
      }
      const newKey = payload.newKey;
      if (typeof newKey !== "string") {
        rejected.push({ tid, reason: "malformed-payload" });
        continue;
      }
      signingKey = newKey;
      lastRotationTid = tid;
      if (typeof payload.newEncryptionKey === "string") {
        encryptionKey = payload.newEncryptionKey;
      }
    } else if (env.$type === DECLARE_KEYS) {
      const declared = payload.encryptionKey;
      if (typeof declared !== "string") {
        rejected.push({ tid, reason: "malformed-payload" });
        continue;
      }
      encryptionKey = declared;
    }

    accepted.push(tid);
  }

  return { signingKey, encryptionKey, accepted, rejected };
}
